import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO } from "@/components/SEO";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Privacy Policy"
        description="Learn how ManageClean collects, uses, and protects your information. Read our comprehensive privacy policy for data security and user rights."
        keywords={["privacy policy", "data protection", "ManageClean privacy"]}
        url="/privacy"
      />
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-8 pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0))]" />
        <div className="container relative z-10 mx-auto px-4">
          <h1 className="mb-4 text-4xl font-bold tracking-tight text-slate-900 md:text-5xl">
            Privacy Policy
          </h1>
          <p className="text-slate-600">
            <strong>Last Updated:</strong> December 27, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="prose prose-slate prose-lg mx-auto max-w-4xl">
            {/* Introduction */}
            <p>
              ManageClean, LLC ("ManageClean," "we," "our," or "us") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard
              your information when you use our platform, ManageClean OS, and related services
              (collectively, the "Services").
            </p>
            <p>
              By accessing or using our Services, you agree to this Privacy Policy. If you do not
              agree with the practices described in this policy, please do not use our Services.
            </p>

            {/* Section 1 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              1. Information We Collect
            </h2>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">Personal Information</h3>
            <p>
              We collect information you voluntarily provide when creating an account or using our
              Services, including:
            </p>
            <ul>
              <li>Name, email address, phone number</li>
              <li>Business name and address</li>
              <li>Payment and billing information (processed securely via Stripe)</li>
              <li>
                Employee information you input into the platform (names, schedules, assignments)
              </li>
              <li>Client and customer information you choose to store</li>
            </ul>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">
              Automatically Collected Information
            </h3>
            <p>When you access our Services, we automatically collect:</p>
            <ul>
              <li>Device information (browser type, operating system, device identifiers)</li>
              <li>Usage data (features accessed, time spent, actions taken)</li>
              <li>IP address and general location information</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">AI Usage Data</h3>
            <p>
              When you use our AI-powered features (Business Assistant, Document Writer), we
              collect:
            </p>
            <ul>
              <li>Prompts and queries you submit</li>
              <li>Generated content and responses</li>
              <li>Credit usage and interaction patterns</li>
            </ul>

            {/* Section 2 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              2. How We Use Your Information
            </h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>
                <strong>Provide and maintain our Services:</strong> Process transactions, manage
                your account, and deliver features you request
              </li>
              <li>
                <strong>Improve and personalize:</strong> Analyze usage patterns, develop new
                features, and customize your experience
              </li>
              <li>
                <strong>Communicate with you:</strong> Send service announcements, updates, security
                alerts, and support messages
              </li>
              <li>
                <strong>Process payments:</strong> Handle subscription billing and credit purchases
                through our payment processor (Stripe)
              </li>
              <li>
                <strong>Ensure security:</strong> Detect, prevent, and respond to fraud, abuse, or
                security incidents
              </li>
              <li>
                <strong>Comply with legal obligations:</strong> Meet regulatory requirements and
                respond to lawful requests
              </li>
            </ul>

            {/* Section 3 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              3. Data Storage and Security
            </h2>
            <p>
              We implement industry-standard security measures to protect your information,
              including:
            </p>
            <ul>
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure cloud infrastructure hosted on Convex</li>
              <li>Regular security assessments and monitoring</li>
              <li>Access controls and authentication via Clerk</li>
            </ul>
            <p>
              While we strive to protect your information, no method of transmission over the
              Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>

            {/* Section 4 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">4. Information Sharing</h2>
            <p>
              We do not sell your personal information. We may share your information in the
              following circumstances:
            </p>
            <ul>
              <li>
                <strong>Service Providers:</strong> We work with trusted third-party providers who
                assist in operating our Services (Clerk for authentication, Stripe for payments,
                Convex for data storage, Google for AI services)
              </li>
              <li>
                <strong>Business Transfers:</strong> If ManageClean is involved in a merger,
                acquisition, or sale of assets, your information may be transferred as part of that
                transaction
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose information if required by law,
                regulation, legal process, or governmental request
              </li>
              <li>
                <strong>Protection of Rights:</strong> We may disclose information to protect the
                rights, property, or safety of ManageClean, our users, or others
              </li>
            </ul>

            {/* Section 5 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              5. Your Rights and Choices
            </h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal
              information:
            </p>
            <ul>
              <li>
                <strong>Access:</strong> Request a copy of the personal information we hold about
                you
              </li>
              <li>
                <strong>Correction:</strong> Request correction of inaccurate or incomplete
                information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your personal information, subject to
                certain exceptions
              </li>
              <li>
                <strong>Data Portability:</strong> Request a copy of your data in a portable format
              </li>
              <li>
                <strong>Opt-out:</strong> Unsubscribe from marketing communications at any time
              </li>
            </ul>
            <p>To exercise these rights, please contact us at support@manageclean.com.</p>

            {/* Section 6 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              6. Cookies and Tracking
            </h2>
            <p>
              We use cookies and similar technologies to enhance your experience. These include:
            </p>
            <ul>
              <li>
                <strong>Essential Cookies:</strong> Required for basic site functionality and
                authentication
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Help us understand how users interact with our
                Services
              </li>
              <li>
                <strong>Preference Cookies:</strong> Remember your settings and preferences
              </li>
            </ul>
            <p>
              You can control cookies through your browser settings, but disabling certain cookies
              may affect functionality.
            </p>

            {/* Section 7 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              7. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new policy on this page and updating the "Last
              Updated" date. Your continued use of our Services after changes become effective
              constitutes acceptance of the revised policy.
            </p>

            {/* Section 8 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">8. Contact Us</h2>
            <p>
              If you have questions or comments about this Privacy Policy, please contact us at:
            </p>
            <p className="font-medium">support@manageclean.com</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
